import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const listenersRef = useRef(new Map());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace(/^https?/, (m) => m === 'https' ? 'wss' : 'ws') + '/api/ws/' + token;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastEvent(data);

          // Dispatch to channel listeners
          const channel = data.channel;
          if (channel && listenersRef.current.has(channel)) {
            listenersRef.current.get(channel).forEach(cb => cb(data));
          }

          // Also dispatch to wildcard listeners
          if (listenersRef.current.has('*')) {
            listenersRef.current.get('*').forEach(cb => cb(data));
          }
        } catch (e) { /* ignore non-JSON messages like pong */ }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Reconnect with backoff
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;

      // Ping every 25 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);

      return () => clearInterval(pingInterval);
    } catch (e) {
      // WebSocket not supported or connection failed
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connect]);

  // Subscribe to a specific channel
  const subscribe = useCallback((channel, callback) => {
    if (!listenersRef.current.has(channel)) {
      listenersRef.current.set(channel, new Set());
    }
    listenersRef.current.get(channel).add(callback);

    // Return unsubscribe function
    return () => {
      const set = listenersRef.current.get(channel);
      if (set) {
        set.delete(callback);
        if (set.size === 0) listenersRef.current.delete(channel);
      }
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ connected, lastEvent, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
};
